import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Scan, Activity, ArrowLeft } from 'lucide-react';
import { api } from '../lib/api';

export default function AR() {
    const navigate = useNavigate();
    const videoRef = useRef<HTMLVideoElement>(null);
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const [stream, setStream] = useState<MediaStream | null>(null);
    const [loading, setLoading] = useState(false);
    const [context, setContext] = useState('');

    const startCamera = async () => {
        try {
            const s = await navigator.mediaDevices.getUserMedia({
                video: { facingMode: 'environment', width: { ideal: 1920 }, height: { ideal: 1080 } }
            });
            setStream(s);
            if (videoRef.current) videoRef.current.srcObject = s;
        } catch (err) {
            alert('Camera initialization failed. Check permissions.');
        }
    };

    const stopCamera = () => {
        if (stream) {
            stream.getTracks().forEach(t => t.stop());
            setStream(null);
        }
    };

    useEffect(() => {
        startCamera();
        return () => stopCamera();
    }, []);

    const handleCapture = async () => {
        if (!videoRef.current || !canvasRef.current) return;

        setLoading(true);
        const ctx = canvasRef.current.getContext('2d');
        canvasRef.current.width = videoRef.current.videoWidth;
        canvasRef.current.height = videoRef.current.videoHeight;
        ctx?.drawImage(videoRef.current, 0, 0);

        canvasRef.current.toBlob(async (blob) => {
            if (!blob) return;
            const file = new File([blob], "visual-capture.jpg", { type: "image/jpeg" });

            try {
                const data = await api.analyzeImage(file, context || 'Environmental scan');
                navigate('/chat', {
                    state: {
                        initialPrompt: context,
                        messages: [
                            { role: 'user', content: `[Spatial Capture] ${context}` },
                            { role: 'assistant', content: data.summary || 'Environmental analysis complete.' }
                        ]
                    }
                });
            } catch (err) {
                alert('Visual analysis timeout.');
            } finally {
                setLoading(false);
            }
        }, 'image/jpeg');
    };

    return (
        <div className="flex-1 bg-black relative flex flex-col items-center justify-center p-6 h-[calc(100vh-64px)] overflow-hidden">
            <button
                onClick={() => navigate(-1)}
                className="absolute top-6 left-6 z-30 p-3 bg-dark/50 backdrop-blur-xl border border-white/10 rounded-xl text-white hover:bg-dark transition-all"
            >
                <ArrowLeft size={20} />
            </button>

            <div className="max-w-4xl w-full relative group">
                <div className="absolute -inset-1 bg-gradient-to-r from-secondary to-primary rounded-2xl blur opacity-30 shadow-[0_0_50px_rgba(132,88,179,0.3)]"></div>

                <div className="relative aspect-video bg-dark rounded-2xl border border-white/10 overflow-hidden shadow-2xl">
                    <video ref={videoRef} autoPlay playsInline muted className="w-full h-full object-cover grayscale opacity-80 group-hover:grayscale-0 group-hover:opacity-100 transition-all duration-700" />
                    <canvas ref={canvasRef} className="hidden" />

                    {loading && (
                        <div className="absolute inset-0 bg-dark/80 backdrop-blur-xl flex flex-col items-center justify-center space-y-4">
                            <Activity className="text-secondary animate-spin" size={48} />
                            <p className="text-secondary font-black uppercase tracking-[0.4em] animate-pulse">Syncing Neural Layer...</p>
                        </div>
                    )}

                    <div className="absolute top-6 right-6 flex items-center gap-2">
                        <div className="px-3 py-1 bg-red-500/80 backdrop-blur-md rounded-full text-[9px] font-black text-white tracking-widest animate-pulse flex items-center gap-2">
                            <div className="w-1.5 h-1.5 bg-white rounded-full" /> SPATIAL FEED ACTIVE
                        </div>
                    </div>
                </div>

                <div className="mt-8 space-y-4">
                    <input
                        type="text"
                        value={context}
                        onChange={(e) => setContext(e.target.value)}
                        disabled={loading}
                        placeholder="Focus context (e.g. 'alternator mounting bolts')..."
                        className="w-full bg-dark border border-slate/30 rounded-xl px-6 py-4 text-white focus:ring-2 focus:ring-secondary/50 focus:border-secondary focus:outline-none shadow-2xl transition-all"
                    />

                    <button
                        onClick={handleCapture}
                        disabled={loading}
                        className="w-full py-5 bg-gradient-to-r from-secondary to-primary text-dark font-black uppercase tracking-[0.3em] rounded-2xl hover:scale-[1.02] active:scale-95 transition-all shadow-2xl flex items-center justify-center gap-3"
                    >
                        <Scan size={24} /> Initialize Capture
                    </button>
                </div>
            </div>

            {/* Retro Scanline Overlay */}
            <div className="absolute inset-0 pointer-events-none opacity-5 bg-[linear-gradient(rgba(18,16,16,0)_50%,rgba(0,0,0,0.25)_50%),linear-gradient(90deg,rgba(255,0,0,0.06),rgba(0,255,0,0.02),rgba(0,0,118,0.06))] bg-[length:100%_2px,3px_100%]" />
        </div>
    );
}
